// src/components/Navbar.tsx
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navbar = () => {
  const [pastThreshold, setPastThreshold] = useState(false);
  const [pastFullThreshold, setPastFullThreshold] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Close menu when location changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const isMobile = window.innerWidth < 768;

    const handleScroll = () => {
      const position = window.scrollY;
      const scrollingDown = position > lastScrollY;

      // On mobile, always show the header bar with menu button
      if (!isMobile) {
        setPastThreshold(position > 20);
        setPastFullThreshold(position > 100);

        // Auto-hide nav only when scrolling down and past the full threshold
        if (pastFullThreshold && !menuOpen) {
          setIsNavVisible(!scrollingDown || position < 50);
        } else {
          setIsNavVisible(true);
        }
      }

      lastScrollY = position;
    };

    // Handle resize to detect mobile/desktop
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      if (mobile) {
        // Force mobile state
        setPastThreshold(true);
        setIsNavVisible(false);
      } else if (window.scrollY <= 20) {
        // Reset to default state on desktop if at top
        setPastThreshold(false);
        setIsNavVisible(true);
      }
    };

    // Run once on mount to set initial state
    handleResize();

    // Show navbar on mouse movement near top of screen (desktop only)
    const handleMouseMove = (e: MouseEvent) => {
      if (!isMobile && e.clientY <= 80 && pastFullThreshold && !isNavVisible) {
        setIsNavVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [pastFullThreshold, menuOpen, isNavVisible]);

  // Define navigation items with route information
  type NavItem = {
    id: string;
    label: string;
    path: string; // Full page paths
  };

  const navItems: NavItem[] = [
    { id: 'about', label: 'About', path: '/about' },
    { id: 'services', label: 'Services', path: '/services' },
    // { id: 'team', label: 'Team', path: '/team' },
    { id: 'contact', label: 'Contact', path: '/contact' },
  ];

  const handleNavItemClick = (item: NavItem, e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default to handle navigation ourselves
    setMenuOpen(false); // Always close menu when navigating

    // Always navigate to the dedicated page
    if (item.path) {
      navigate(item.path);
      // Scroll to top after navigation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Animation variants
  const navVariants = {
    initial: {
      y: 0,
      height: 100,
      paddingTop: 48,
      paddingBottom: 16,
    },
    scrolled: {
      y: 0,
      height: 70,
      paddingTop: 8,
      paddingBottom: 8,
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
    },
    hidden: {
      y: -100,
    },
    visible: {
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30,
      },
    },
    minimized: {
      y: 0,
      height: 'auto',
      paddingTop: 12,
      paddingBottom: 12,
    },
  };

  const menuButtonVariants = {
    open: { rotate: 90, scale: 1 },
    closed: { rotate: 0, scale: 1 },
  };

  const fullScreenMenuVariants = {
    hidden: {
      opacity: 0,
      clipPath: 'circle(0% at calc(100% - 3rem) 3rem)',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
        staggerChildren: 0,
        when: 'afterChildren',
      },
    },
    visible: {
      opacity: 1,
      clipPath: 'circle(150% at calc(100% - 3rem) 3rem)',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.1,
        delayChildren: 0.2,
        when: 'beforeChildren',
      },
    },
  };

  const menuItemVariants = {
    hidden: {
      y: 50,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
  };

  const logoVariants = {
    large: { scale: 1 },
    small: { scale: 0.9 },
  };

  // Check if an item should be highlighted as active
  const isActive = (item: NavItem) => {
    return location.pathname === item.path;
  };

  return (
    <>
      {/* Main Navbar */}
      <motion.header
        ref={navRef}
        className={cn(
          'fixed top-0 left-0 right-0 z-40 transition-all',
          'hidden md:block', // Always hide on mobile, conditionally show on desktop
          menuOpen ? 'pointer-events-none' : '',
        )}
        variants={navVariants}
        initial="initial"
        animate={pastThreshold ? 'hidden' : 'initial'}
        key="navbar">
        <div className="container px-4 mx-auto flex items-center justify-between text-zinc-600/80">
          {/* Logo - hidden when menu is open */}
          <div
            className={cn(
              'flex items-center z-20',
              menuOpen ? 'opacity-0' : 'opacity-100',
              'transition-opacity duration-300',
            )}>
            <motion.div
              variants={logoVariants}
              animate={pastThreshold ? 'small' : 'large'}
              transition={{ duration: 0.3 }}>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="cursor-pointer">
                <img
                  src="/hoptira-logo-full-dark.svg"
                  alt="Hoptira Logo"
                  className="h-8 md:h-10 transition-all duration-300"
                />
              </a>
            </motion.div>
          </div>

          {/* Centered Navigation (visible on large screens when not scrolled far) */}
          <div
            className={cn(
              'hidden md:block absolute left-1/2 transform -translate-x-1/2',
              pastFullThreshold ? 'opacity-0' : 'opacity-100',
              'transition-opacity duration-300',
            )}>
            <nav
              className={cn('flex gap-4', pastThreshold ? 'gap-2' : 'gap-4')}>
              {navItems.map((item) => (
                <motion.div
                  key={item.id}
                  whileHover={{ y: -2 }}
                  className={cn(
                    'cursor-pointer font-semibold relative px-4 py-2 rounded-full transition-all',
                    "after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-secondary after:transition-all hover:after:w-1/2",
                    pastThreshold ? 'text-base' : 'text-2xl',
                    isActive(item) ? 'text-secondary after:w-1/2' : '',
                  )}>
                  <a
                    href={item.path}
                    onClick={(e) => handleNavItemClick(item, e)}
                    className={cn(isActive(item) ? 'text-secondary' : '')}>
                    {item.label}
                  </a>
                </motion.div>
              ))}
            </nav>
          </div>

          {/* Get Quote Button (visible on large screens when not scrolled far) */}
          <div
            className={cn(
              'hidden md:block',
              pastFullThreshold ? 'opacity-0' : 'opacity-100',
              'transition-opacity duration-300',
            )}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}>
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/contact');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}>
                <Button
                  variant="default"
                  className={cn(
                    'ml-4 font-semibold transition-all duration-300',
                    pastThreshold ? 'text-base py-2 px-4' : 'text-xl py-6 px-6',
                  )}>
                  Get Quote
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Header Bar with Logo and Menu Button - always visible on mobile */}
      <motion.div
        className="fixed top-0 left-0 right-0 z-50 md:hidden flex items-center justify-between p-6 bg-white/90 backdrop-blur-sm"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}>
        {/* Mobile logo - only visible when menu is closed */}
        {!menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="cursor-pointer">
              <img
                src="/hoptira-logo-full-dark.svg"
                alt="Hoptira Logo"
                className="h-8"
              />
            </a>
          </motion.div>
        )}

        {/* Empty div to ensure menu button stays on the right when logo is hidden */}
        {menuOpen && <div className="w-8" />}

        {/* Menu button */}
        <Button
          onClick={() => setMenuOpen(!menuOpen)}
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg">
          <motion.div
            variants={menuButtonVariants}
            initial="closed"
            animate={menuOpen ? 'open' : 'closed'}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
            {menuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </motion.div>
        </Button>
      </motion.div>

      {/* Desktop Menu Button - only visible when scrolled */}
      <AnimatePresence>
        {pastThreshold && (
          <motion.div
            className="fixed top-4 right-4 z-50 pointer-events-auto hidden md:block"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}>
            <Button
              onClick={() => setMenuOpen(!menuOpen)}
              variant="secondary"
              size="icon"
              className="h-12 w-12 rounded-full shadow-lg">
              <motion.div
                variants={menuButtonVariants}
                initial="closed"
                animate={menuOpen ? 'open' : 'closed'}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                {menuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </motion.div>
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full Screen Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 bg-white z-40 flex flex-col justify-center items-center"
            variants={fullScreenMenuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden">
            <div className="container mx-auto px-4">
              <div className="flex flex-col items-center justify-center text-center">
                {/* Logo in fullscreen menu */}
                <motion.div className="mb-12" variants={menuItemVariants}>
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      setMenuOpen(false);
                      navigate('/');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                    <img
                      src="/hoptira-logo-full-dark.svg"
                      alt="Hoptira Logo"
                      className="h-12"
                    />
                  </a>
                </motion.div>

                {/* Menu Items */}
                <nav className="space-y-8 mb-12">
                  {navItems.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={menuItemVariants}
                      whileHover={{ scale: 1.05 }}
                      className="overflow-hidden">
                      <a
                        href={item.path}
                        onClick={(e) => handleNavItemClick(item, e)}
                        className={`text-4xl md:text-5xl font-bold hover:text-secondary transition-colors ${
                          isActive(item) ? 'text-secondary' : 'text-gray-800'
                        }`}>
                        {item.label}
                      </a>
                    </motion.div>
                  ))}
                </nav>

                {/* CTA Button */}
                <motion.div
                  variants={menuItemVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}>
                  <a
                    href="/contact"
                    onClick={(e) => {
                      e.preventDefault();
                      setMenuOpen(false);
                      navigate('/contact');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                    <Button
                      variant="secondary"
                      className="text-xl py-6 px-10 font-semibold mt-6">
                      Get Quote
                    </Button>
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
